import { getHeadersWithToken } from './api.js';
import { showToast } from './toast.js';
import { pushEvent } from './analytics.js';

const CartManager = (() => {
    let state = {
        isAddingToCart: false,
        cartCount: null
    };

    const SELECTORS = {
        cartCountBadges: '.profile-avatar__item-count, .item-count',
        cartCountMobile: '.item-count-mobile'
    };

    // todo: this resize observer should be moved to header.js as it does not seem related to the cart
    // Initialize ResizeObserver to automatically update main padding when header height changes
    (() => {
        const header = document.querySelector('.header-nav');
        const main = document.querySelector('main');
        
        if (!header || !main) return;
        
        const observer = new ResizeObserver(() => {
            // header.js does some weird stuff where it can replace the entire header DOM element.
            // that appears to trigger this observer, but the "offsetHeight" is then 0.
            // we must make sure to re-query the header nav in this callback so that we have the element that's on the page.
            
            const header = document.querySelector('.header-nav');
            if (!header) return;
            main.style.paddingTop = `${header.offsetHeight}px`;
        });
        
        observer.observe(header);
    })();

    // Helper: Check auth and handle 401 responses
    async function handleAuthResponse(response) {
        if (response.status === 401) {
            handleAuthRequired();
            return true;
        }
        return false;
    }

    // Helper: Refresh cart count and update UI
    async function refreshAndUpdateCartCount() {
        const newCount = await getCartCount();
        if (newCount !== null) {
            updateCartCountDisplay(newCount);
        }
        return newCount;
    }

    // Helper: Execute cart operation with common workflow
    async function executeCartOperation(operation) {
        if (state.isAddingToCart) {
            return { success: false, error: 'ALREADY_ADDING' };
        }

        state.isAddingToCart = true;

        try {
            const headers = await getHeadersWithToken({
                'Content-Type': 'application/json'
            });

            const result = await operation(headers);
            
            if (result.authRequired) {
                return { success: false, error: 'AUTH_REQUIRED' };
            }

            if (result.success) {
                const newCount = await refreshAndUpdateCartCount();
                return { ...result, count: newCount };
            }

            return result;

        } catch (error) {
            console.error('Error in cart operation:', error);
            return { success: false, error: error.message };
        } finally {
            state.isAddingToCart = false;
        }
    }

    // Add a sample to cart using a known sample code (AddToCart → GetCartCount → Update UI)
    // productInfo (required): { itemName, itemCollection, itemColor } for GTM tracking
    // Returns: { success: boolean, count?: number, error?: string }
    async function addSampleToCart(sampleCode, quantity = 1, { itemName, itemCollection, itemColor }) {
        return executeCartOperation(async (headers) => {
            // Add to cart
            const addResponse = await fetch('/api/cart/AddToCart', {
                method: 'POST',
                headers: await getHeadersWithToken({
                    'Content-Type': 'application/json'
                }),
                body: JSON.stringify({ Code: sampleCode, Quantity: quantity })
            });

            if (await handleAuthResponse(addResponse)) {
                return { authRequired: true };
            }

            if (!addResponse.ok) {
                try {
                    const errorData = await addResponse.json();
                    console.error('AddToCart error response:', errorData);
                    // Backend returns { type: string, message: string }
                    return { 
                        success: false, 
                        error: errorData.type || 'ValidationError',
                        message: errorData.message || 'Failed to add item to cart.'
                    };
                } catch (e) {
                    console.error('Failed to parse error response:', e);
                    return { 
                        success: false, 
                        error: 'UnknownError',
                        message: 'Failed to add item to cart. Please try again.'
                    };
                }
            }

            // Success - track GTM event
            pushEvent({
                event: 'add_to_cart',
                ecommerce: {
                    currency: 'USD',
                    value: 0,
                    items: [
                        {
                            item_id: sampleCode,
                            item_name: itemName || '',
                            item_collection: itemCollection || '',
                            item_color: itemColor || '',
                            quantity: quantity
                        }
                    ]
                }
            });

            return { success: true };
        });
    }

    /**
     * Adds the best sample for a product variant (colorway or style) to the cart.
     * <br/>
     * The backend will take care of selecting the best sample.
     * @param productVariantCode A colorway or style catalog code.
     * @param quantity - The number of samples to add (default: 1).
     * @returns {Promise<{success: boolean, error: string}|{success: boolean, error: string}|{[p: string]: *}|*|{success: boolean, error: *}|undefined>}
     */
    async function addBestSampleToCart(productVariantCode, quantity = 1) {
        return executeCartOperation(async (_) => {
            // Add to cart
            const addResponse = await fetch('/api/cart/AddBestSampleToCart', {
                method: 'POST',
                headers: await getHeadersWithToken({
                    'Content-Type': 'application/json'
                }),
                body: JSON.stringify({ Code: productVariantCode, Quantity: quantity })
            });

            if (await handleAuthResponse(addResponse)) {
                return { authRequired: true };
            }

            if (!addResponse.ok) {
                try {
                    const errorData = await addResponse.json();
                    console.error('AddBestSampleToCart error response:', errorData);
                    // Backend returns { type: string, message: string }
                    return {
                        success: false,
                        error: errorData.type || 'ValidationError',
                        message: errorData.message || 'Failed to add item to cart.'
                    };
                } catch (e) {
                    console.error('Failed to parse error response:', e);
                    return {
                        success: false,
                        error: 'UnknownError',
                        message: 'Failed to add item to cart. Please try again.'
                    };
                }
            }
            
            const json = await addResponse.json();

            // Success - track GTM event
            pushEvent({
                event: 'add_to_cart',
                ecommerce: {
                    currency: 'USD',
                    value: 0,
                    items: [
                        {
                            item_id: json.sampleCatalogCode,
                            item_name: json.patternFamilyName || '',
                            item_collection: json.collectionName || '',
                            item_color: json.colorwayName || '',
                            item_style: json.variantType === 'Style' ? json.variantName : '',
                            quantity: quantity
                        }
                    ]
                }
            });

            return { ...json, success: true };
        });
    }

    // Get current cart item count from server
    async function getCartCount() {
        try {
            const response = await fetch('/api/cart/GetCartCount', {
                headers: await getHeadersWithToken()
            });
            
            if (!response.ok) {
                console.error('Failed to get cart count:', response.statusText);
                return null;
            }

            const data = await response.json();
            state.cartCount = data.count;
            return data.count;
        } catch (error) {
            console.error('Error getting cart count:', error);
            return null;
        }
    }

    // Refresh cart count from server and update display
    async function refreshCartCount() {
        const count = await getCartCount();
        if (count !== null) {
            updateCartCountDisplay(count);
        }
        return count;
    }

    // Update all cart count badges in the DOM
    function updateCartCountDisplay(count) {
        const badges = document.querySelectorAll(SELECTORS.cartCountBadges);
        const mobileBadges = document.querySelectorAll(SELECTORS.cartCountMobile);
        
        badges.forEach(badge => {
            badge.textContent = count;
            badge.style.display = count > 0 ? '' : 'none';
        });

        mobileBadges.forEach(badge => {
            badge.textContent = `(${count})`;
        });

        state.cartCount = count;
    }

    // Handle authentication required - trigger sign-in flyout or redirect
    function handleAuthRequired() {
        // Try to open sign-in flyout (existing modal system)
        const signInButton = document.querySelector('[data-flyout-trigger="SignInForm"]');
        if (signInButton) {
            signInButton.click();
        } else {
            // Fallback: redirect to account page if flyout not found
            const returnUrl = encodeURIComponent(window.location.pathname);
            window.location.href = `/en/account?returnUrl=${returnUrl}`;
        }
    }

    // Helper: Get user-friendly error message (fallback for client-side errors)
    function getClientErrorMessage(errorType) {
        switch (errorType) {
            case 'AUTH_REQUIRED':
                return 'Please sign in to order samples.';
            case 'ALREADY_ADDING':
                return 'Please wait while we add the item to your cart.';
            default:
                return 'Unable to add sample to cart. Please try again.';
        }
    }

    // Add multiple samples to cart using known sample codes
    // Returns: { success: boolean, successCount: number, failureCount: number, results: [], authRequired?: boolean }
    async function addSamplesToCart(sampleCodes) {
        if (!Array.isArray(sampleCodes) || sampleCodes.length === 0) {
            return { success: false, successCount: 0, failureCount: 0, results: [] };
        }

        return executeCartOperation(async (headers) => {
            // Bulk add to cart
            const addResponse = await fetch('/api/cart/BulkAddToCart', {
                method: 'POST',
                headers: await getHeadersWithToken({
                    'Content-Type': 'application/json'
                }),
                body: JSON.stringify({ Codes: sampleCodes, Quantity: 1 })
            });

            if (await handleAuthResponse(addResponse)) {
                return { authRequired: true };
            }

            if (!addResponse.ok) {
                const errorText = await addResponse.text();
                console.error('BulkAddToCart error response:', errorText);
                return { success: false, successCount: 0, failureCount: sampleCodes.length, results: [] };
            }

            const result = await addResponse.json();

            return {
                success: result.successCount > 0,
                successCount: result.successCount,
                failureCount: result.failureCount,
                results: result.results
            };
        });
    }

    // Get current state (for debugging)
    function getState() {
        return { ...state };
    }
    
    async function getBestSampleCode(catalogCode) {
        const url = `/api/catalog/bestsample?code=${catalogCode}`;
        const response = await fetch(url, {
            headers: await getHeadersWithToken()
        });
        
        if (response.status === 404) {
            console.debug('Best sample not found:', catalogCode);
            return null;
        }
        
        if (!response.ok) {
            console.error('Failed to get best sample code:', response.statusText);
            return null;
        }
        
        return await response.json();
    }

    return {
        addBestSampleToCart,
        addSampleToCart,
        addSamplesToCart,
        getBestSampleCode,
        getCartCount,
        refreshCartCount,
        updateCartCountDisplay,
        showToast,
        handleAuthRequired,
        getClientErrorMessage,
        getState
    };
})();

export default CartManager;
